import { SymbolIcon } from "@radix-ui/react-icons";
import {
  Box,
  Button,
  Callout,
  Flex,
  Heading,
  Separator,
  Strong,
  Text,
} from "@radix-ui/themes";
import { useMutation } from "@tanstack/react-query";
import { addMinutes, isPast } from "date-fns";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useRecoilState } from "recoil";
import {
  BookingEndResponse,
  END_BOOKING_PROCESS_AVAILABLE_DELAY_AFTER_START_MINUTES,
  RequirementStatus,
  RequirementType,
} from "use-smart-locks-shared";
import {
  endBooking,
  getApiUserErrorMessage,
} from "../../../shared/api/nest-api";
import { LockState } from "../../../shared/bluetooth/bluetooth-ilockit.service";
import { ContactUsLink } from "../../../shared/components/contact-us-link";
import { useGeolocation } from "../../../shared/hooks";
import { useGenericErrorHandler } from "../../../shared/hooks/use-generic-error-handler";
import { unlockLocksOfBookingResponseState } from "../../../shared/state/booking-auth-data-map.state";
import { useCampingBooking } from "../../hooks/use-camping-booking";
import { EndBookingFeedback } from "./end-booking-feedback.component";

type CounterInfo = {
  current: number;
  total: number;
};

type EndBookingProps = {
  hasEBike: boolean;
  getLockStatus: (unitId: string) => Promise<LockState | null>;
  isLockOperationInProgress: boolean;
  onEndBookingStateChange: (isInProgress: boolean) => void;
};

function ReturnSteps({ hasEBike }: { hasEBike: boolean }) {
  const { hasBluetoothLock, hasLock } = useCampingBooking();

  const steps = hasBluetoothLock
    ? [
        "🏠 Fahrrad an einer Station abstellen",
        "🔗 Schloss schließen",
        ...(hasEBike ? ["🔌 Fahrrad an den Strom anschließen"] : []),
        ...(hasLock ? ["🚪 Station schließen"] : []),
        "✅ “Fahrrad zurückgeben” drücken",
      ]
    : [
        "🔓 Eingangstür und Schlüsselkästen erneut öffnen",
        "🔗 Fahrräder abschließen und anstecken",
        "🔑 Ggf. Schlüssel in die Schlüsselkästen legen",
        "✅ “Fahrräder zurückgeben” drücken",
      ];

  return (
    <Callout.Root>
      <Callout.Text>
        <Text as="div" size="2">
          <ol style={{ paddingLeft: "1rem" }}>
            {steps.map((step, index) => (
              <li key={index}>{step}</li>
            ))}
          </ol>
        </Text>
      </Callout.Text>
    </Callout.Root>
  );
}

export function EndBooking({
  hasEBike,
  getLockStatus,
  isLockOperationInProgress,
  onEndBookingStateChange,
}: EndBookingProps) {
  const {
    booking,
    hasMultipleUnits,
    hasBluetoothLock,
    requiresLocationVerification,
  } = useCampingBooking();
  const { handleError } = useGenericErrorHandler();
  const { error: locationError, getLocationOrThrowError } = useGeolocation();
  const [, setUnlockLocksOfBookingResponseCache] = useRecoilState(
    unlockLocksOfBookingResponseState(booking.id),
  );

  const unitText = hasMultipleUnits ? "Fahrräder" : "Fahrrad";
  const buttonText = `${unitText} zurückgeben`;
  const genericErrorText = `Bitte schließen Sie alle Fahrräder an und legen Sie alle Schlüssel zurück in die Boxen. Drücken Sie dann auf “${buttonText}”.`;
  const bluetoothErrorText = hasMultipleUnits
    ? "Bitte schließen Sie alle Schlösser, bevor Sie die Buchung beenden."
    : "Bitte schließen Sie das Schloss, bevor Sie die Buchung beenden.";
  const infoText = hasBluetoothLock ? bluetoothErrorText : genericErrorText;

  const { mutateAsync: endBookingMutation, isPending } = useMutation({
    mutationFn: async () => {
      const location = requiresLocationVerification
        ? await getLocationOrThrowError()
        : undefined;

      return endBooking(booking.id, location);
    },
    onError(error) {
      toast.error(
        getApiUserErrorMessage(error) ??
          "Leider ist ein unerwarteter Fehler aufgetreten.",
      );
    },
  });

  const [infoState, setInfoState] = useState<"default" | "error" | "success">(
    "default",
  );
  const [unitsConnectedInfo, setUnitsConnectedInfo] =
    useState<CounterInfo | null>(null);
  const [keysInPlaceInfo, setKeysInPlaceInfo] = useState<CounterInfo | null>(
    null,
  );
  const [correctLocationsInfo, setCorrectLocationsInfo] =
    useState<CounterInfo | null>(null);
  const [doorClosedInfo, setDoorClosedInfo] = useState<CounterInfo | null>(
    null,
  );

  const [receivedUnexpectedError, setReceivedUnexpectedError] =
    useState<boolean>(false);
  const [isCheckingLock, setIsCheckingLock] = useState(false);

  useEffect(() => {
    if (booking.endedAt) {
      setInfoState("success");
    }
  }, [booking.endedAt]);

  const [enableEndBooking, setEnableEndBooking] = useState(false);
  useEffect(() => {
    const evaluateVisibility = () => {
      setEnableEndBooking(
        booking.startedAt
          ? isPast(
              addMinutes(
                booking.startedAt,
                END_BOOKING_PROCESS_AVAILABLE_DELAY_AFTER_START_MINUTES,
              ),
            )
          : false,
      );
    };

    evaluateVisibility();
    const interval = setInterval(evaluateVisibility, 10_000);
    return () => clearInterval(interval);
  }, [booking.startedAt]);

  const areAllBluetoothLocksClosed = async () => {
    if (!hasBluetoothLock) return true;

    setIsCheckingLock(true);
    try {
      const bluetoothUnits = booking.units.filter((u) => u.hasBluetoothLock);
      if (bluetoothUnits.length === 0) return true;

      for (const unit of bluetoothUnits) {
        const status = await getLockStatus(unit.id);
        if (status !== LockState.Closed) {
          return false;
        }
      }
      return true;
    } catch (error) {
      handleError(
        error,
        "Beim prüfen der Schlösser ist ein unerwarteter Fehler aufgetreten. Bitte versuchen Sie es erneut oder kontaktieren Sie die Service-Hotline.",
      );
      return false;
    } finally {
      setIsCheckingLock(false);
    }
  };

  const endBookingWrapper = async () => {
    onEndBookingStateChange(true);
    try {
      const allClosed = await areAllBluetoothLocksClosed();
      if (!allClosed) {
        setInfoState("error");
        setUnitsConnectedInfo(null);
        setKeysInPlaceInfo(null);
        setCorrectLocationsInfo(null);
        setDoorClosedInfo(null);
        return;
      }

      const result = await endBookingMutation();
      if (result.success) {
        setInfoState("success");
        setUnlockLocksOfBookingResponseCache(null);
        return;
      }

      setInfoState("error");
      const allCounts = getCountsOfAllRequirements(result);
      setUnitsConnectedInfo(
        allCounts.charging.total > 0 ? allCounts.charging : null,
      );
      setKeysInPlaceInfo(
        allCounts.keyreturn.total > 0 ? allCounts.keyreturn : null,
      );
      setCorrectLocationsInfo(
        allCounts.location.total > 0 ? allCounts.location : null,
      );
      setDoorClosedInfo(
        allCounts.doorClosed.total > 0 ? allCounts.doorClosed : null,
      );

      const anyError = result.unitStatuses
        .flatMap((unitStatus) => unitStatus.requirements)
        .find((requirement) => requirement.status === RequirementStatus.Error);
      if (anyError) {
        toast.error(
          anyError.reason ??
            "Es ist ein unerwarteter Fehler aufgetreten. Bitte versuchen Sie es erneut oder kontaktieren Sie die Service-Hotline.",
        );
      }
      setReceivedUnexpectedError(!!anyError);
    } finally {
      onEndBookingStateChange(false);
    }
  };

  return (
    enableEndBooking && (
      <Flex direction="column" gap="4" minWidth="100%">
        <Separator my="4" className="full-width" />
        <Flex direction="column" gap="4" minWidth="100%">
          <Heading size="3">Rückgabe:</Heading>
          <ReturnSteps hasEBike={hasEBike} />
          {locationError && (
            <Callout.Root color="red" mb="4">
              <Callout.Text>{locationError}</Callout.Text>
            </Callout.Root>
          )}
          {infoState === "error" && (
            <FinishBookingInfoError
              {...{
                unitsConnectedInfo,
                keysInPlaceInfo,
                doorClosedInfo,
                locationInfo: correctLocationsInfo,
                infoText,
              }}
            />
          )}
          {infoState === "success" && <EndBookingFeedback />}
          {infoState !== "success" && (
            <Button
              onClick={() => void endBookingWrapper()}
              // TODO: make it a little more obious that the locks are unlocked
              color="red"
              loading={isPending || isCheckingLock}
              disabled={isLockOperationInProgress}
            >
              {buttonText}
            </Button>
          )}
          {receivedUnexpectedError && <ContactUsLink />}
        </Flex>
      </Flex>
    )
  );
}

function getCountsOfAllRequirements(
  endBookingResult: BookingEndResponse,
): Record<RequirementType, CounterInfo> {
  const result: Record<RequirementType, CounterInfo> = {
    charging: { current: 0, total: 0 },
    keyreturn: { current: 0, total: 0 },
    doorClosed: { current: 0, total: 0 },
    location: { current: 0, total: 0 },
  };

  endBookingResult.unitStatuses
    .flatMap((unitStatus) => unitStatus.requirements)
    .filter(
      (requirement) => requirement.status !== RequirementStatus.NotApplicable,
    )
    .forEach((requirement) => {
      result[requirement.type].total++;
      if (requirement.status === RequirementStatus.Fulfilled) {
        result[requirement.type].current++;
      }
    });
  return result;
}

function FinishBookingInfoError({
  unitsConnectedInfo,
  keysInPlaceInfo,
  doorClosedInfo,
  locationInfo,
  infoText,
}: {
  /** Pass null to hide this information. */
  unitsConnectedInfo: CounterInfo | null;
  /** Pass null to hide this information. */
  keysInPlaceInfo: CounterInfo | null;
  /** Pass null to hide this information. */
  doorClosedInfo: CounterInfo | null;
  /** Pass null to hide this information. */
  locationInfo: CounterInfo | null;
  infoText: string;
}) {
  const showUnitsConnected = unitsConnectedInfo !== null;
  const showKeysInPlace = keysInPlaceInfo !== null;
  const showDoorClosed = doorClosedInfo !== null;
  const showLocation = locationInfo !== null;
  const showDetails =
    showUnitsConnected || showKeysInPlace || showDoorClosed || showLocation;

  const unitsConnectedText = showUnitsConnected
    ? `${unitsConnectedInfo.current.toString()} / ${unitsConnectedInfo.total.toString()} Fahrräder angeschlossen`
    : "";
  const keysInPlaceText = showKeysInPlace
    ? `${keysInPlaceInfo.current.toString()} / ${keysInPlaceInfo.total.toString()} Schlüssel zurückgelegt`
    : "";
  const doorClosedText = showDoorClosed
    ? `${doorClosedInfo.current.toString()} / ${doorClosedInfo.total.toString()} Station geschlossen`
    : "";
  const locationText = showLocation
    ? `${locationInfo.current.toString()} / ${locationInfo.total.toString()} Standort validiert`
    : "";

  const renderInfoListItem = (text: string, info: CounterInfo | null) => {
    if (!text) return null;
    return (
      <li>
        {info && info.current < info.total ? <Strong>{text}</Strong> : text}
      </li>
    );
  };

  return (
    <Callout.Root color="red">
      <Callout.Icon>
        <SymbolIcon />
      </Callout.Icon>
      {/* 
        Spreading the 'as' property is a hackfix to overwrite the element of the text node.
        Otherwise, the nested 'ul' results in an error: "Warning: validateDOMNesting(...): <ul> cannot appear as a descendant of <p>."
      */}
      <Callout.Text {...{ as: "div" }}>
        {infoText}
        {showDetails && (
          <Box ml="6" mt="1">
            <ul>
              {renderInfoListItem(unitsConnectedText, unitsConnectedInfo)}
              {renderInfoListItem(keysInPlaceText, keysInPlaceInfo)}
              {renderInfoListItem(doorClosedText, doorClosedInfo)}
              {renderInfoListItem(locationText, locationInfo)}
            </ul>
          </Box>
        )}
      </Callout.Text>
    </Callout.Root>
  );
}
